import PropTypes from "prop-types";
import { useState } from "react";
import TextInput from "@shared/v2/TextInput";
import Button from "@shared/v2/Button";
import Tooltip from "@shared/v2/Tooltip";
import { CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import ImageDropTarget from "../shared/ImageDropTarget";
import FeaturedImageHelpModal from "../shared/FeaturedImageHelpModal";
import { useSiteUuid, usePageUuid } from "../../reducers/websiteReducer/areaPageSelectors";

const FeaturedImagePanel = ({ data = undefined, setFeaturedImageData }) => {
  const initialPage = { title: "", area: "", featuredImageUrl: "" };
  const [page, setPage] = useState(initialPage);
  const siteUuid = useSiteUuid();
  const pageUuid = usePageUuid();
  const [featuredImageModalIsOpen, setFeaturedImageModalIsOpen] = useState(false);

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("siteUuid", siteUuid);
    formData.append("pageUuid", pageUuid);
    formData.append("componentUuid", data.uuid);

    caseTransformingAxios.post("/website/upload_image_to_aws", formData).then((res) => {
      setFeaturedImageData({
        ...data,
        imageUrl: res?.data?.url,
      });
    });
  };

  return (
    <>
      <CollapsibleWebsiteCard
        title="Featured Image"
        action={
          <Button schema="tertiary" onClick={() => setFeaturedImageModalIsOpen(true)}>
            <CircleInfoSolidV6 size="s" /> Help
          </Button>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-[24px]">
          {data?.imageUrl && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <img
                className="tw-w-full tw-h-[150px] tw-rounded-[8px] tw-object-cover"
                src={data?.imageUrl}
                alt="Featured"
              />
              <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
                <Button
                  className="tw-flex-1"
                  schema="warning"
                  size="small"
                  onClick={() => setFeaturedImageData({ ...data, imageUrl: null })}
                >
                  Delete
                </Button>
                <Button
                  className="tw-flex-1"
                  size="small"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = (e) => {
                      const featuredImageUrl = e.target.files[0];
                      uploadFile(featuredImageUrl);
                    };
                    input.click();
                  }}
                >
                  Replace
                </Button>
              </div>
            </div>
          )}
          {!data?.imageUrl && <ImageDropTarget onChange={(file) => uploadFile(file)} />}
          <TextInput
            autoComplete="off"
            placeholder="Ex. Luxury home at night"
            label={
              <>
                Alt Text{" "}
                <Tooltip
                  innerClassName="!tw-text-left tw-max-w-[160px]"
                  trigger={<CircleInfoSolidV6 />}
                  content="Add a description to images on your page to improve SEO and make your website more ADA compliant."
                />
              </>
            }
            disabled={!page?.featuredImageUrl}
            onChange={(e) => {
              const featuredImageAltText = e.target.value || "";
              setPage((p) => ({ ...p, featuredImageAltText }));
            }}
            value={page?.featuredImageAltText || ""}
          />
        </div>
      </CollapsibleWebsiteCard>
      <FeaturedImageHelpModal
        open={featuredImageModalIsOpen}
        onClose={() => setFeaturedImageModalIsOpen(false)}
      />
    </>
  );
};

FeaturedImagePanel.propTypes = {
  data: PropTypes.shape({
    uuid: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  setFeaturedImageData: PropTypes.func.isRequired,
};

export default FeaturedImagePanel;
